import onboardingStatusService from './onboardingStatus';

/**
 * Post-login destination resolver
 * -----------------------------------------------------------------------------
 * Product rule (2026-07): a brand-new / empty operation is guided to the
 * "Primeiros Passos" onboarding page so the operator can register the minimum
 * data (aircraft, pilots, WhatsApp) needed to start using Zulbot. Once the
 * operation has any real data, the default landing after login becomes
 * Mission Control.
 *
 * "Empty" is derived from the operational entity counts returned by
 * `/onboarding/status/` (aircraft, pilots, flights). We intentionally ignore
 * the WhatsApp / safety / fixed-cost flags here: those can be true on an
 * otherwise empty account and do not, on their own, indicate the operator has
 * started scheduling operations.
 *
 * The resolver never throws: any failure (network, timeout, unexpected shape)
 * falls back to Mission Control so a flaky status call can't strand the user
 * on a blank screen right after authentication.
 */

export const PRIMEIROS_PASSOS_PATH = '/primeiros-passos';
export const ACCOUNT_FINANCE_SETUP_PATH = '/account/finance-setup';
export const MISSION_CONTROL_PATH = '/mission-control';

// Guard the redirect against a slow status endpoint. A brand-new account only
// needs a few COUNT queries, so this is comfortably above the expected latency
// while still keeping the post-login navigation snappy on a cold backend.
const RESOLVE_TIMEOUT_MS = 2500;

/**
 * True when the onboarding status describes an operation with no aircraft,
 * pilots or flights yet — i.e. a fresh account that has not started setup.
 *
 * A missing / malformed status returns `false` so the caller defaults to the
 * established-operation destination (Mission Control) rather than pushing an
 * existing operator into the onboarding guide on a bad payload.
 */
export function isEmptyOperation(status) {
  if (!status || typeof status !== 'object') return false;
  const aircraft = Number(status.aircraft_count) || 0;
  const pilots = Number(status.pilot_count) || 0;
  const flights = Number(status.flight_count) || 0;
  return aircraft === 0 && pilots === 0 && flights === 0;
}

/**
 * Resolve where a freshly-authenticated operator should land.
 *
 * @param {object} [options]
 * @param {number} [options.timeoutMs] Max time to wait for the status call
 *   before falling back to Mission Control.
 * @returns {Promise<string>} A route path (never rejects).
 */
export async function resolvePostLoginPath({ timeoutMs = RESOLVE_TIMEOUT_MS } = {}) {
  try {
    const status = await Promise.race([
      onboardingStatusService.getStatus(),
      new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    // Only the explicit boolean false triggers the new authenticated setup.
    // Missing/malformed fields from an older backend preserve the established
    // destination contract instead of blocking an existing account.
    if (status && typeof status === 'object' && status.persona_configured === false) {
      return ACCOUNT_FINANCE_SETUP_PATH;
    }
    // A financial membership is deliberately invisible until the invited user
    // accepts it. Route only an explicit positive server count to the personal
    // decision screen; no aircraft or partner data is opened by this redirect.
    if (
      status
      && typeof status === 'object'
      && Number.isInteger(status.finance_invites_pending_count)
      && status.finance_invites_pending_count > 0
    ) {
      return ACCOUNT_FINANCE_SETUP_PATH;
    }
    return isEmptyOperation(status) ? PRIMEIROS_PASSOS_PATH : MISSION_CONTROL_PATH;
  } catch {
    return MISSION_CONTROL_PATH;
  }
}

export default resolvePostLoginPath;
